import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import {
  MODULE_CONFIG,
  formatCurrency,
  formatDate,
  formatTime,
  capitalize,
} from "./shared";
import {
  FiArrowLeft,
  FiCopy,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiCreditCard,
  FiDollarSign,
  FiUser,
  FiMail,
  FiPhone,
  FiFileText,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch transaction detail
  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    loader(true);

    try {
      const endpoint = `${MODULE_CONFIG.endpoints.detail}?id=${id}`;
      const res = await ApiClient.get(endpoint);

      if (res?.success && (res?.data || res?.data?.transaction)) {
        const data = res?.data?.transaction || res?.data?.data || res?.data;
        setTransaction(data);
      } else {
        toast.error("Unable to find transaction record.");
      }
    } catch (err) {
      console.error("Failed to fetch transaction detail:", err);
      toast.error("Failed to load transaction details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Copy text helper
  const handleCopy = (text, label = "Text") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard!`);
  };

  if (loading && !transaction) {
    return (
      <Layout>
        <div className="py-24 text-center">
          <div className="w-10 h-10 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-black text-slate-900">Loading Transaction Dossier...</p>
        </div>
      </Layout>
    );
  }

  if (!transaction) {
    return (
      <Layout>
        <div className="bg-white rounded-2xl p-8 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] text-center space-y-4 max-w-lg mx-auto my-12">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-300 text-slate-700 flex items-center justify-center mx-auto">
            <FiAlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-base font-black text-slate-900">Transaction Not Found</h2>
          <p className="text-xs font-semibold text-slate-700">
            The requested transaction record could not be loaded or may have been deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate(MODULE_CONFIG.routeBase)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#115B9D] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span>Return to Transactions</span>
          </button>
        </div>
      </Layout>
    );
  }

  // Extract fields safely
  const orderRef =
    transaction?.orderId ||
    transaction?.transactionId ||
    transaction?.stripePaymentIntentId ||
    id?.slice(-8);

  const statusKey = (
    transaction?.transactionStatus ||
    transaction?.status ||
    "completed"
  ).toLowerCase();

  const statusConfig = MODULE_CONFIG.statusMap[statusKey] || {
    label: capitalize(statusKey),
    bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
    dotClass: "bg-slate-600",
  };

  const amount = transaction?.amount ?? 0;
  const currency = transaction?.currency || "USD";
  const fee = transaction?.fee ?? transaction?.platformFee ?? 0;
  const netAmount = transaction?.netAmount ?? (amount - fee);

  const customerName =
    transaction?.customerName ||
    transaction?.name ||
    transaction?.customer?.name ||
    "Direct Customer";
  const customerEmail =
    transaction?.customerEmail ||
    transaction?.email ||
    transaction?.customer?.email ||
    "";
  const customerPhone =
    transaction?.customerPhone ||
    transaction?.phone ||
    transaction?.customer?.phone ||
    "";

  const tenantName =
    transaction?.tenantName ||
    transaction?.tenantId?.name ||
    transaction?.tenantId?.companyName ||
    "Direct Platform";
  const tenantId =
    typeof transaction?.tenantId === "object"
      ? transaction?.tenantId?._id || transaction?.tenantId?.id
      : transaction?.tenantId || "—";

  const paymentMethod =
    transaction?.paymentMethod ||
    transaction?.payment_method ||
    transaction?.method ||
    "Card / Online";
  const cardBrand = transaction?.cardBrand || transaction?.brand || "";
  const cardLast4 = transaction?.last4 || transaction?.cardLast4 || "";

  const planName =
    transaction?.planName ||
    transaction?.plan?.name ||
    transaction?.description ||
    "Standard Subscription Service";

  const createdAt = transaction?.createdAt || transaction?.date;
  const updatedAt = transaction?.updatedAt;

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & BREADCRUMB ACTIONS                       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Transactions List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Transaction #{orderRef}
                </h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-wide border whitespace-nowrap shadow-2xs ${statusConfig.bgClass}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusConfig.dotClass}`} />
                  {statusConfig.label}
                </span>
              </div>
              <p className="text-xs font-bold text-slate-700">
                Payment Dossier & Financial Summary
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            <button
              type="button"
              onClick={() => handleCopy(orderRef, "Order ID")}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-xs"
              title="Copy Order ID"
            >
              <FiCopy className="w-3.5 h-3.5 text-slate-700" />
              <span>Copy Order ID</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
              title="Print Receipt"
            >
              <FiFileText className="w-3.5 h-3.5 text-white" />
              <span>Print Dossier</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. BALANCED DOSSIER LAYOUT (ROW 1: HALF-HALF, ROW 2: FULL) */}
        {/* ======================================================== */}
        <div className="space-y-6">
          {/* ROW 1: 50/50 Balanced Half-Half Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {/* Card 1: Payment & Commercial Overview */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-5">
              <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                  <FiDollarSign className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Financial & Payment Summary
                </h3>
              </div>

              {/* Big Amount Tile */}
              <div className="bg-slate-50 rounded-xl p-4 sm:p-5 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-bold uppercase text-slate-600 block">
                    Total Amount Charged
                  </span>
                  <div className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                    {formatCurrency(amount, currency)}
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:text-right flex-wrap">
                  <span className="uppercase text-xs font-black tracking-wider px-2.5 py-1 rounded bg-white text-slate-900 border border-slate-300">
                    Currency: {currency}
                  </span>
                  <span className="uppercase text-xs font-black tracking-wider px-2.5 py-1 rounded bg-white text-slate-900 border border-slate-300">
                    {paymentMethod}
                  </span>
                </div>
              </div>

              {/* Detailed Breakdown Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-600 block">Platform Net Volume</span>
                  <span className="text-sm font-black text-slate-950">
                    {formatCurrency(netAmount, currency)}
                  </span>
                </div>

                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-600 block">Gateway / Processing Fee</span>
                  <span className="text-sm font-black text-slate-950">
                    {fee > 0 ? formatCurrency(fee, currency) : "Included / $0.00"}
                  </span>
                </div>

                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-600 block">Payment Method</span>
                  <div className="flex items-center gap-1.5 font-black text-slate-900">
                    <FiCreditCard className="w-3.5 h-3.5 text-[#115B9D]" />
                    <span className="uppercase">
                      {cardBrand ? `${cardBrand} ` : ""}
                      {cardLast4 ? `•••• ${cardLast4}` : paymentMethod}
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-600 block">Transaction Status</span>
                  <div>{statusConfig.label}</div>
                </div>
              </div>
            </div>

            {/* Card 2: Tenant & Customer Dossier */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-4">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200 flex items-center justify-center font-bold">
                    <FiUser className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Tenant & Customer Dossier
                  </h3>
                </div>

                <div className="space-y-3.5 text-xs pt-4">
                  {/* Tenant Org */}
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                    <span className="text-[10px] font-bold text-slate-600 uppercase block">
                      Tenant Organization
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 font-black text-slate-950">
                        <HiOutlineBuildingOffice2 className="w-3.5 h-3.5 text-[#115B9D]" />
                        <span>{tenantName}</span>
                      </div>
                      {tenantId && tenantId !== "—" && (
                        <span className="text-[10px] font-mono text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200 truncate max-w-[120px]">
                          ID: {tenantId}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between py-1 border-b border-slate-100">
                      <span className="font-bold text-slate-600">Customer Name:</span>
                      <span className="font-black text-slate-950">{customerName}</span>
                    </div>

                    {customerEmail && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Email:</span>
                        <a
                          href={`mailto:${customerEmail}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1 truncate max-w-[200px]"
                        >
                          <FiMail className="w-3 h-3 shrink-0" />
                          <span className="truncate">{customerEmail}</span>
                        </a>
                      </div>
                    )}

                    {customerPhone && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Phone:</span>
                        <a
                          href={`tel:${customerPhone}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1"
                        >
                          <FiPhone className="w-3 h-3 shrink-0" />
                          <span>{customerPhone}</span>
                        </a>
                      </div>
                    )}

                    <div className="flex items-center justify-between py-1 border-b border-slate-100">
                      <span className="font-bold text-slate-600">Created:</span>
                      <span className="font-bold text-slate-900">
                        {createdAt ? `${formatDate(createdAt)} at ${formatTime(createdAt)}` : "—"}
                      </span>
                    </div>

                    {updatedAt && (
                      <div className="flex items-center justify-between py-1">
                        <span className="font-bold text-slate-600">Last Updated:</span>
                        <span className="font-bold text-slate-900">
                          {formatDate(updatedAt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2: Full Width Product / Service Specification */}
          <div className="w-full bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
            <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                <FiFileText className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                Product / Service Specification
              </h3>
            </div>

            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-900">{planName}</h4>
                  <p className="text-xs font-medium text-slate-700">
                    {transaction?.notes ||
                      transaction?.description ||
                      "Super Admin SaaS subscription billing transaction."}
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <span className="text-[10px] font-bold text-slate-600 uppercase block">
                    Total Charge
                  </span>
                  <span className="text-base font-black text-slate-950">
                    {formatCurrency(amount, currency)}
                  </span>
                </div>
              </div>

              {Array.isArray(transaction?.items) && transaction.items.length > 0 && (
                <div className="space-y-2 pt-2">
                  <span className="text-xs font-bold text-slate-700 uppercase block">
                    Line Items ({transaction.items.length})
                  </span>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                    {transaction.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-xs p-3 rounded-xl bg-white border border-slate-200"
                      >
                        <span className="font-semibold text-slate-800">
                          {item?.name || item?.description || `Item #${idx + 1}`}
                        </span>
                        <span className="font-black text-slate-900">
                          {formatCurrency(item?.amount || item?.price, currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
